<template>
<Transition name="modal-fade">
    <div v-if="isOpen" class="ui-modal-container" :data-modal-id="modalId" @click="handleContainerClick" role="dialog"
    aria-modal="true" :aria-labelledby="`${modalId}-title`"
    :aria-describedby="`${modalId}-description`" tabindex="-1" ref="modalRef">
        <div class="ui-modal__content" :class="[{ 'with-header': title || subtitle }, customClass]">
            <!-- Заголовок модального окна -->
            <header v-if="title || subtitle" class="ui-modal__header">
                <h2 v-if="title" class="ui-modal__title" :id="`${modalId}-title`">
                    {{ title }}
                </h2>
                <p v-if="subtitle" class="ui-modal__subtitle">
                    {{ subtitle }}
                </p>
            </header>

            <!-- Кнопка закрытия -->
            <button v-if="showCloseButton" class="ui-modal__close-btn" @click.prevent="closeModal" aria-label="Закрыть модальное окно">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
                    <rect class="stroke-svg" x="0.5" y="1" width="47" height="47" rx="23.5" stroke="#252628"/>
                    <path class="fill-svg" fill-rule="evenodd" clip-rule="evenodd" d="M15.9999 16.5C16.3904 16.1095 17.0236 16.1095 17.4141 16.5L23.7782 22.8642L30.1421 16.5003C30.5326 16.1098 31.1657 16.1098 31.5563 16.5003C31.9468 16.8909 31.9468 17.524 31.5563 17.9146L25.1925 24.2784L31.5563 30.6422C31.9468 31.0327 31.9468 31.6659 31.5563 32.0564C31.1657 32.4469 30.5326 32.4469 30.1421 32.0564L23.7782 25.6926L17.4141 32.0567C17.0236 32.4472 16.3904 32.4472 15.9999 32.0567C15.6094 31.6662 15.6094 31.033 15.9999 30.6425L22.364 24.2784L15.9999 17.9143C15.6094 17.5237 15.6094 16.8906 15.9999 16.5Z" fill="#252628"/>
                </svg>
            </button>

            <!-- Основное содержимое -->
            <div class="ui-modal__body" :id="`${modalId}-description`">
                <slot></slot>
            </div>

            <!-- Футер с действиями -->
            <footer v-if="$slots.footer" class="ui-modal__footer">
                <slot name="footer"></slot>
            </footer>
        </div>
    </div>
</Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

// Определение props
const props = defineProps({
    customClass: {
        type: String,
        default: '',
        required: false
    },
    modalId: {
        type: String,
        required: true
    },
    title: {
        type: String,
        default: ''
    },
    subtitle: {
        type: String,
        default: ''
    },
    showCloseButton: {
        type: Boolean,
        default: true
    },
    preventScroll: {
        type: Boolean,
        default: true
    }
})

// Определение событий
const emit = defineEmits([
    'modal-open',
    'modal-close'
])

// Состояние модального окна
const isOpen = ref(false)
const modalRef = ref(null)

// Функции для управления скроллом
const scrollManager = {
    scrollPosition: 0,
    scrollbarWidth: 0,

    calculateScrollbarWidth() {
        const outer = document.createElement('div')
        outer.style.visibility = 'hidden'
        outer.style.overflow = 'scroll'
        document.body.appendChild(outer)

        const inner = document.createElement('div')
        outer.appendChild(inner)

        this.scrollbarWidth = outer.offsetWidth - inner.offsetWidth
        outer.parentNode?.removeChild(outer)
        document.documentElement.style.setProperty('--scrollbar-width', `${this.scrollbarWidth}px`);
    },

    lockScroll() {
        this.calculateScrollbarWidth()
        this.scrollPosition = window.pageYOffset
        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.top = `-${this.scrollPosition}px`
        document.body.style.width = '100%'
        document.body.style.paddingRight = `${this.scrollbarWidth}px`
    },

    unlockScroll() {
        document.body.style.removeProperty('overflow')
        document.body.style.removeProperty('position')
        document.body.style.removeProperty('top')
        document.body.style.removeProperty('width')
        document.body.style.removeProperty('padding-right')
        window.scrollTo(0, this.scrollPosition)
        document.documentElement.style.setProperty('--scrollbar-width', `0px`);
    }
}

// Открытие модального окна
const openModal = async () => {
    isOpen.value = true
    if (props.preventScroll) {
        scrollManager.lockScroll()
    }
    await nextTick()
    emit('modal-open')
}

// Закрытие модального окна
const closeModal = () => {
    isOpen.value = false
    if (props.preventScroll) {
        setTimeout(() => {
            scrollManager.unlockScroll()
        }, 300)
    }
    emit('modal-close')
}

// Обработчики событий
const handleEscKey = (event) => {
    if (event.key === 'Escape' && isOpen.value) {
        closeModal()
    }
}

const handleContainerClick = (event) => {
    if (event.target.classList.contains('ui-modal-container')) {
        closeModal()
    }
}

// Менеджер триггеров
const triggerManager = {
    getTriggers() {
        return document.querySelectorAll(`[data-modal-trigger="${props.modalId}"]`)
    },

    handleTriggerClick(event) {
        event.preventDefault()
        openModal()
    },

    bindTriggers() {
        this.getTriggers().forEach(trigger => {
            trigger.addEventListener('click', this.handleTriggerClick)
            trigger.setAttribute('role', 'button')
            trigger.setAttribute('aria-haspopup', 'dialog')
            trigger.setAttribute('aria-expanded', 'false')
        })
    },

    unbindTriggers() {
        this.getTriggers().forEach(trigger => {
            trigger.removeEventListener('click', this.handleTriggerClick)
            trigger.removeAttribute('aria-expanded')
        })
    }
}

// Инициализация при монтировании
onMounted(() => {
    triggerManager.bindTriggers()
    document.addEventListener('keydown', handleEscKey)
})

// Очистка при размонтировании
onUnmounted(() => {
    triggerManager.unbindTriggers()
    document.removeEventListener('keydown', handleEscKey)

    if (isOpen.value && props.preventScroll) {
        scrollManager.unlockScroll()
    }
})

// Публичные методы компонента
defineExpose({
    open: openModal,
    close: closeModal,
    isOpen
})
</script>